import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { headingColor, headingFont, headingSm, textColor, textMd } from '@insify/design-system/typography';
import clsx from 'clsx';

import * as styles from './RichContent.module.css';
import { createContentRenderer, replaceSpaceBeforePunctuationsWithNBSP } from './createContentRenderer';

import type { Options as ReactRendererOptions, RenderNode } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import type React from 'react';

type RichContentType = {
  content: { json: Document };
};

type RichContentProps = ReactRendererOptions & {
  className?: string;
  /** Rich content from contentful JSON object */
  content?: RichContentType;
};

type Props = RichContentProps & {
  /** React.FC that will fetch tooltip content by a tooltip id */
  tooltip?: React.FC<{ tooltipId: string; children: React.ReactNode }>;
};

const MockTooltip = ({ tooltipId }: { tooltipId: string }) => <div>{tooltipId}</div>;

const RichContent = ({
  content,
  renderMark,
  renderNode,
  renderText = (text) => text,
}: RichContentProps): React.ReactElement | null => {
  if (content === undefined) {
    return null;
  }

  return (
    <>
      {createContentRenderer({
        renderNode,
        renderMark,
        renderText: (text: string) => renderText(replaceSpaceBeforePunctuationsWithNBSP(text)),
      })(content.content.json)}
    </>
  );
};

const DSHeading2 = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <h2 className={clsx(headingSm, headingColor, headingFont, className)}>{children}</h2>
);

const DSParagraph = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <p className={clsx(textMd, textColor, className)}>{children}</p>
);

RichContent.H2 = DSHeading2;
RichContent.P = DSParagraph;

const RichContentWithTooltip: React.FC<Props> = ({
  content,
  renderNode,
  renderMark,
  renderText = (text) => text,
  className = '',
  tooltip: Tooltip = MockTooltip,
}: Props) => {
  const defaultRenderNode: RenderNode = {
    [BLOCKS.HEADING_2]: (_, children): JSX.Element => (
      <RichContent.H2 className={styles.title}>{children}</RichContent.H2>
    ),
    [BLOCKS.PARAGRAPH]: (_, children): JSX.Element => <RichContent.P className={styles.copy}>{children}</RichContent.P>,
    [BLOCKS.UL_LIST]: (_, children): JSX.Element => <ul className={styles.ulList}>{children}</ul>,
    [INLINES.ENTRY_HYPERLINK]: (node, children): JSX.Element => {
      return <Tooltip tooltipId={node.data.target.sys.id}>{children}</Tooltip>;
    },
  };

  return (
    <div className={clsx(styles.wrapper, className)}>
      <RichContent {...{ renderNode: { ...defaultRenderNode, ...renderNode }, renderMark, renderText, content }} />
    </div>
  );
};

const RichContentIntro = (props: Props) => {
  return <RichContentWithTooltip {...props} className={clsx(props.className, styles.richContentIntro)} />;
};

const DSHeadingIntro2 = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <h2 className={clsx(headingSm, headingColor, headingFont, className, styles.richContentIntro, styles.title)}>
    {children}
  </h2>
);

const DSParagraphIntro = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <p className={clsx(textMd, textColor, className, styles.richContentIntro, styles.copy)}>{children}</p>
);

RichContentIntro.H2 = DSHeadingIntro2;
RichContentIntro.P = DSParagraphIntro;

export { MockTooltip, RichContent, RichContentWithTooltip, RichContentIntro };

export type { RichContentType, RichContentProps };
